import React from 'react';
import { fetchDocs } from '@services/docs';
import { useDispatch } from 'react-redux';
import { showErrorBanner } from '@utils/application';
import { addUrlParam } from '@utils';

const initialState = {
  docsData: { metadata: {}, docs: null },
  loadingCompanies: [],

  companyCachedDocs: {},
  tickerCachedDocs: {},
};

const actionTypes = {
  SET_LOADING_COMPANIES: 'SET_LOADING_COMPANIES',

  SET_DOCS_DATA: 'SET_DOCS_DATA',

  SET_COMPANY_CACHED_DOCS: 'SET_COMPANY_CACHED_DOCS',
  SET_TICKER_CACHED_DOCS: 'SET_TICKER_CACHED_DOCS',
};

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.SET_DOCS_DATA: {
      return {
        ...state,
        docsData: action.payload,
      };
    }
    case actionTypes.SET_LOADING_COMPANIES: {
      if (Array.isArray(action.payload)) {
        return { ...state, loadingCompanies: [...state.loadingCompanies, ...action.payload] };
      }
      return { ...state, loadingCompanies: [...state.loadingCompanies, action.payload] };
    }
    case actionTypes.SET_COMPANY_CACHED_DOCS: {
      const { company, docs } = action.payload;
      return {
        ...state,
        loadingCompanies: state.loadingCompanies.filter((l) => !isSameCompany(l, company)),
        companyCachedDocs: { ...state.companyCachedDocs, [company.doc_tag_id]: docs },
      };
    }
    case actionTypes.SET_TICKER_CACHED_DOCS: {
      const { company, docs } = action.payload;
      return {
        ...state,
        loadingCompanies: state.loadingCompanies.filter((l) => !isSameCompany(l, company)),
        tickerCachedDocs: { ...state.tickerCachedDocs, [company.company_id]: docs },
      };
    }
    default:
      return state;
  }
};

export function fetchCommonTickersDocs(filters) {
  let params = '';
  params = addUrlParam(params, 'category=company');
  params = addUrlParam(params, `tickers=${filters.tickers}`);
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}

export function fetchCommonCompanyDocs(company, filters) {
  let params = '';
  params = addUrlParam(params, 'category=company');
  if (isPublicCompany(company)) {
    params = addUrlParam(params, `tickers=${company.symbol}`);
  } else {
    params = addUrlParam(params, `doc_tag_ids=${company.doc_tag_id}`);
  }
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}

export function fetchNewsTickersDocs(filters) {
  let params = '';
  params = addUrlParam(params, 'category=news');
  params = addUrlParam(params, `tickers=${filters.tickers}`);
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}
export function fetchNewsCompanyDocs(company, filters) {
  let params = '';
  params = addUrlParam(params, 'category=news');
  if (isPublicCompany(company)) {
    params = addUrlParam(params, `tickers=${company.symbol}`);
  } else {
    params = addUrlParam(params, `doc_tag_ids=${company.doc_tag_id}`);
  }
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}

export function fetchUserTickersDocs(filters) {
  let params = '';
  params = addUrlParam(params, 'category=user');
  params = addUrlParam(params, `tickers=${filters.tickers}`);
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}
export function fetchUserCompanyDocs(company, filters) {
  let params = '';
  params = addUrlParam(params, 'category=user');
  if (isPublicCompany(company)) {
    params = addUrlParam(params, `tickers=${company.symbol}`);
  } else {
    params = addUrlParam(params, `doc_tag_ids=${company.doc_tag_id}`);
  }
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}

export function fetchUserDocs(filters) {
  let params = '';
  params = addUrlParam(params, 'category=user');
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}

const convertFiltersToParams = (filters, params) => {
  if (!filters) {
    return params;
  }
  const { doc_types } = filters;
  if (doc_types) {
    params = addUrlParam(params, `doc_types=${doc_types}`);
  }
  if (filters.docMode === 'credit') {
    const doc_types = 'Credit Agreement,Loan Agreement,Indentures,Prospectus,Agreements';
    params = addUrlParam(params, `doc_types=${doc_types}`);
  }
  if (filters.docMode === 'ppm') {
    const doc_types = 'PPM,Side Letter';
    params = addUrlParam(params, `doc_types=${doc_types}`);
  }
  return params;
};

export function fetchFedDocs(filters) {
  let params = '';
  params = addUrlParam(params, 'category=macro');
  params = convertFiltersToParams(filters, params);
  return fetchDocs(params);
}

export function fetchCompanyDocs(company, filters) {
  return Promise.all([
    fetchCommonCompanyDocs(company, filters),
    fetchNewsCompanyDocs(company, filters),
    fetchUserCompanyDocs(company, filters),
  ]).then((responses) => {
    let docs = [];
    responses.forEach((r) => {
      if (r) {
        docs = [...docs, ...r];
      }
    });
    return docs;
  });
}

export function fetchTickersDocs(filters) {
  return Promise.all([
    fetchCommonTickersDocs(filters),
    fetchNewsTickersDocs(filters),
    fetchUserTickersDocs(filters),
  ]).then((responses) => {
    let docs = [];
    responses.forEach((r) => {
      if (r) {
        docs = [...docs, ...r];
      }
    });
    return docs;
  });
}

function getCompanyDocs(state, company) {
  if (isPublicCompany(company)) {
    return state.tickerCachedDocs[company.company_id];
  }
  return state.companyCachedDocs[company.doc_tag_id];
}

function isSameCompany(company1, company2) {
  if (!company1 || !company2) {
    return false;
  }
  if (isPublicCompany(company1)) {
    return company1.company_id === company2.company_id;
  }
  return company1.doc_tag_id === company2.doc_tag_id;
}

function isSameFilters(filters1, filters2) {
  return JSON.stringify(filters1) === JSON.stringify(filters2);
}

function isCompanyDocsLoading(state, company) {
  let found = false;
  state.loadingCompanies.forEach((l) => {
    if (isSameCompany(l, company)) {
      found = true;
    }
  });
  return found;
}

function isPublicCompany(company) {
  return !!company.company_id;
}

function filterOutCompaniesAlreadyCachedOrLoading(companies, state) {
  return companies.filter((c) => !getCompanyDocs(state, c) && !isCompanyDocsLoading(state, c));
}

function generateCompanyDocsDataPayload(activeCompany, filters, companyDocs) {
  const metadata = { company: activeCompany, filters };
  const docs = companyDocs;
  return { metadata, docs };
}

function isPayloadActive(payload, state) {
  const v =
    isSameCompany(state.docsData.metadata.company, payload.metadata.company) &&
    isSameFilters(state.docsData.metadata.filters, payload.metadata.filters);
  return v;
}

export const useDocsCache = (companies, activeCompany, filters) => {
  const appDispatch = useDispatch();
  const [state, dispatch] = React.useReducer(reducer, initialState, () => initialState);

  const fetchAndStoreCompanyDocs = async (company) => {
    dispatch({ type: actionTypes.SET_LOADING_COMPANIES, payload: company });
    const docs = await fetchCompanyDocs(company, filters);
    if (docs) {
      dispatch({
        type: isPublicCompany(company) ? actionTypes.SET_TICKER_CACHED_DOCS : actionTypes.SET_COMPANY_CACHED_DOCS,
        payload: { company, docs },
      });
    } else {
      showErrorBanner('Error loading documents. Please remove company and re-add.', null, appDispatch);
    }
  };

  React.useEffect(() => {
    // prefetch docs for all companies
    const companiesToFetch = filterOutCompaniesAlreadyCachedOrLoading(companies, state);
    companiesToFetch.forEach((c) => fetchAndStoreCompanyDocs(c));
  }, [companies, state]);

  React.useEffect(() => {
    // set docs for active company
    if (activeCompany) {
      const companyDocs = getCompanyDocs(state, activeCompany);
      if (companyDocs) {
        const payload = generateCompanyDocsDataPayload(activeCompany, filters, companyDocs);
        if (!isPayloadActive(payload, state)) {
          dispatch({ type: actionTypes.SET_DOCS_DATA, payload });
        }
      } else {
        if (!isCompanyDocsLoading(state, activeCompany)) {
          fetchCompanyDocs(activeCompany, filters);
        }
      }
    }
  }, [state, activeCompany, filters]);

  return state;
};
